import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Bell, AlertTriangle, Info, X, Loader2 } from 'lucide-react';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from '@/hooks/notifications-hooks';
import type { Notification } from '@/services/notifications-api';

const NotificationsModule: React.FC = () => {
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [viewingNotification, setViewingNotification] = useState<Notification | null>(null);

  const { data, isLoading, isError } = useNotifications();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();

  const notifications: Notification[] = data?.results ?? [];

  const filtered = notifications.filter((n) => {
    const matchesType = filterType === 'all' || n.notification_type === filterType;
    const matchesSeverity = filterSeverity === 'all' || n.severity === filterSeverity;
    return matchesType && matchesSeverity;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkAsRead = (id: number) => {
    markAsRead.mutate(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleDelete = (id: number) => {
    deleteNotification.mutate(id, {
      onSuccess: () => {
        if (viewingNotification?.id === id) setViewingNotification(null);
      },
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'overdue':
      case 'fine':
        return <AlertTriangle className="h-4 w-4" />;
      case 'system':
      case 'maintenance':
        return <Info className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'urgent':
        return 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)]';
      case 'warning':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-[var(--lib-navy)]/10 text-[var(--lib-navy)]';
    }
  };

  const getTypeVariant = (type: string): 'destructive' | 'secondary' | 'outline' => {
    if (type === 'overdue' || type === 'fine') return 'destructive';
    if (type === 'system' || type === 'maintenance') return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Notifications</h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Alerts and system messages.</p>
        </div>
        <Button variant="outline" onClick={handleMarkAllAsRead} disabled={unreadCount === 0 || markAllAsRead.isPending}>
          {markAllAsRead.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Mark All as Read
        </Button>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {(['all', 'overdue', 'return_due', 'hold_available', 'fine_incurred', 'fine_paid', 'system', 'maintenance', 'other'] as const).map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
              filterType === type
                ? 'bg-[var(--lib-gold)] text-[var(--lib-on-gold)]'
                : 'border border-[var(--lib-hairline)] text-[var(--lib-ink-muted)] hover:border-gray-300'
            }`}
          >
            {type === 'all' ? 'All' : type.replace('_', ' ')}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {(['all', 'info', 'warning', 'urgent'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterSeverity(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                filterSeverity === s
                  ? 'bg-gray-800 text-white'
                  : 'border border-[var(--lib-hairline)] text-[var(--lib-ink-muted)] hover:border-gray-300'
              }`}
            >
              {s === 'all' ? 'All severities' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Notification list */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="py-12 flex items-center justify-center gap-2 text-sm text-[var(--lib-ink-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading notifications…
          </div>
        ) : isError ? (
          <div className="py-12 text-center text-sm text-[var(--lib-danger)]">Failed to load notifications.</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--lib-ink-muted)]">No notifications</div>
        ) : (
          filtered.map((n) => {
            const isUnread = !n.is_read;
            return (
              <div
                key={n.id}
                className={`flex items-start gap-4 px-5 py-4 border-b border-[var(--lib-hairline)] last:border-b-0 group hover:bg-[var(--lib-surface-2)] transition-colors ${
                  isUnread ? 'bg-[#FFFBEB] border-l-2 border-l-[var(--lib-gold)]' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  n.severity === 'urgent' ? 'bg-[var(--lib-danger)]/10' : 'bg-[var(--lib-gold)]/10'
                }`}>
                  <Bell className={`h-4 w-4 ${n.severity === 'urgent' ? 'text-[var(--lib-danger)]' : 'text-[var(--lib-gold)]'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${isUnread ? 'font-semibold text-[var(--lib-ink)]' : 'font-medium text-[var(--lib-ink-muted)]'}`}>
                    {n.title}
                  </div>
                  <div className="text-sm text-[var(--lib-ink-muted)] mt-0.5">{n.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge variant={getTypeVariant(n.notification_type)} className="text-xs py-0">
                      <span className="flex items-center gap-1">
                        {getTypeIcon(n.notification_type)}
                        <span className="capitalize">{n.notification_type.replace('_', ' ')}</span>
                      </span>
                    </Badge>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(n.severity)}`}>
                      {n.severity}
                    </span>
                    {n.action_required && (
                      <span className="text-xs text-[var(--lib-danger)] font-medium">Action required</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-[var(--lib-ink-muted)] font-mono">
                    {new Date(n.created_at).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    {isUnread && (
                      <button
                        onClick={() => handleMarkAsRead(n.id)}
                        className="text-xs text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] px-2 py-1 rounded hover:bg-[var(--lib-surface-2)]"
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      onClick={() => setViewingNotification(n)}
                      className="text-xs text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] px-2 py-1 rounded hover:bg-[var(--lib-surface-2)]"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(n.id)}
                      className="text-[var(--lib-ink-muted)] hover:text-[var(--lib-danger)] p-1 rounded hover:bg-[var(--lib-danger)]/10"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* View Notification Dialog */}
      <Dialog open={!!viewingNotification} onOpenChange={() => setViewingNotification(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Notification Details</DialogTitle>
          </DialogHeader>

          {viewingNotification && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant={getTypeVariant(viewingNotification.notification_type)}>
                  <div className="flex items-center gap-1">
                    {getTypeIcon(viewingNotification.notification_type)}
                    <span className="capitalize">{viewingNotification.notification_type.replace('_', ' ')}</span>
                  </div>
                </Badge>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(viewingNotification.severity)}`}>
                  {viewingNotification.severity.toUpperCase()}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">{viewingNotification.title}</h3>
                <p className="text-[var(--lib-ink-muted)] whitespace-pre-wrap">{viewingNotification.message}</p>
              </div>

              <div className="border-t pt-3 space-y-2 text-sm">
                <div><span className="font-medium">Time:</span> {new Date(viewingNotification.created_at).toLocaleString()}</div>
                <div><span className="font-medium">Status:</span> {viewingNotification.is_read ? 'Read' : 'Unread'}</div>
                {viewingNotification.action_required && (
                  <div className="text-[var(--lib-danger)] font-medium">This notification requires action</div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingNotification(null)}>Close</Button>
            {viewingNotification && !viewingNotification.is_read && (
              <Button onClick={() => { handleMarkAsRead(viewingNotification.id); setViewingNotification(null); }}>
                Mark as Read
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsModule;
